import { go } from 'connected-react-router';
import { get } from 'lodash';
import requester from 'utils/requester';
import config from 'config';
import api from 'config/api';
import platform from 'platform';
import { showLoader, hideLoader } from 'store/loader/actions';
import { showModal, hideModal } from 'store/modal/actions';
import { MODAL_TYPES } from 'constants/modal-types';
import { pagesActions } from 'store/pages';
import { appActions } from 'store/app';
import { toastActions } from 'store/toast';
import { setToken, clearToken } from 'utils/auth-token';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import {
  LOGIN_USER,
  LOGIN_USER_ERROR,
  LOAD_DATA,
  LOAD_DATA_SUCCESS,
  LOAD_DATA_ERROR,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER,
  RESET_DATA,
  SET_PC_LEVEL_SUCCEESS,
  SET_PC_STATUS_SUCCESS,
  SET_PIN_SUCCESS,
} from './action-types';

type LoginPayload = {
  email: string;
  password: string;
  errorModal?: {
    returnFocusTo: string;
  };
};

const ERROR = {
  CREDENTIALS: 'invalid credentials',
  GRANT_TYPE: 'invalid grant type',
  CLIENT_ID: 'invalid client ext_id',
  DEVICE_ID: 'invalid device extid',
};

const CREDENTIALS_MSG =
  'The username or password is incorrect. Please, try again with different credentials.';

export const loginUser = (payload: LoginPayload) => async (dispatch) => {
  const { email, password, errorModal } = payload;
  try {
    dispatch(showLoader());
    dispatch({ type: LOGIN_USER });

    const { data: authToken } = await requester({
      method: 'post',
      url: `${api.host}${api.endpoints.auth}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      extendHeadersWithSession: false,
      params: {
        grant_type: 'password',
        username: email,
        client_id: config.client_id,
        device_id: platform.getUID(),
        password,
      },
    });

    setToken(authToken, false);

    dispatch(loginUserSuccess());
    dispatch(pagesActions.resetData());

    await dispatch(loadProfileData());
    // back to profile
    dispatch(go(-2));
  } catch (error) {
    let message = get(error, ['response', 'data', 'error', 'message'], error.message);

    if (message === ERROR.CREDENTIALS) message = CREDENTIALS_MSG;

    if (errorModal) {
      dispatch(
        showModal(MODAL_TYPES.INFO, {
          message,
          onModalBack() {
            dispatch(hideModal());
            errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
          },
        }),
      );
    }
    dispatch({ type: LOGIN_USER_ERROR, payload: message });
  } finally {
    dispatch(hideLoader());
  }
};

export const loginUserSuccess = () => ({ type: LOGIN_USER_SUCCESS });

export const logoutUser = () => (dispatch) => {
  try {
    dispatch({ type: LOGOUT_USER });
    clearToken();
    dispatch(pagesActions.resetData());
    dispatch(appActions.fetchGuestAuthToken());
  } catch (error) {
    console.error(error);
  }
};

export const loadProfileData = () => async (dispatch) => {
  try {
    dispatch({ type: LOAD_DATA });
    const { data } = await requester({
      method: 'get',
      url: `${api.host}${api.endpoints.profile}`,
    });

    const { data: parentalControl } = await requester({
      method: 'get',
      url: `${api.host}${api.endpoints.pc}`,
    });

    const { data: subscriptions } = await requester({
      method: 'get',
      url: `${api.host}${api.endpoints.subs}`,
    });

    const payload = { data, parentalControl, subscriptions };

    dispatch({ type: LOAD_DATA_SUCCESS, payload });
  } catch (error) {
    console.error(error);
    dispatch({ type: LOAD_DATA_ERROR, payload: error.message });
  }
};

export const setPCLimit = (level: number) => async (dispatch) => {
  try {
    await requester({
      method: 'patch',
      url: `${api.host}${api.endpoints.pc}`,
      data: { pc_level: level },
    });

    dispatch({ type: SET_PC_LEVEL_SUCCEESS, payload: { level } });
    dispatch(toastActions.showToast({ text: 'Settings were saved' }));
  } catch (error) {
    console.error(error);
  }
};

export const setPCStatus = (status: boolean) => async (dispatch) => {
  try {
    await requester({
      method: 'patch',
      url: `${api.host}${api.endpoints.pc}`,
      data: { pc: status },
    });

    dispatch({ type: SET_PC_STATUS_SUCCESS, payload: { status } });
    dispatch(toastActions.showToast({ text: 'Settings were saved' }));
  } catch (error) {
    console.error(error);
  }
};

export const setPin = (pin: string) => async (dispatch) => {
  try {
    await requester({
      method: 'patch',
      url: `${api.host}${api.endpoints.profile}`,
      data: { pc_pin: pin },
    });

    dispatch({ type: SET_PIN_SUCCESS, payload: { pin } });
    dispatch(toastActions.showToast({ text: 'Settings were saved' }));
  } catch (error) {
    console.error(error);
  }
};

export const resetData = () => ({ type: RESET_DATA });
